"use client";

import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import { Home, AlertCircle } from 'lucide-react';
import { RiCustomerServiceFill } from "react-icons/ri";
import { useState, useEffect } from 'react';

// Dynamically import Lottie to avoid SSR issues
const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

export default function NotFound() {
  const router = useRouter();
  const [animationData, setAnimationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Mark this page as a 404 page to hide chatbot
    document.body.setAttribute('data-page-type', 'not-found');
    
    // Load the 404.json animation file
    fetch('/404.json')
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to load animation');
        }
        return res.json();
      })
      .then((data) => {
        setAnimationData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading 404 animation:', err);
        setError(true);
        setLoading(false);
      });

    // Cleanup: remove the attribute when component unmounts
    return () => {
      document.body.removeAttribute('data-page-type');
    };
  }, []);

  return (
    <div style={{ height: '98vh', width: '99vw', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb', padding: '0', margin: '0', overflow: 'hidden' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'clamp(16px, 4vw, 24px)', margin: '0', padding: 'clamp(16px, 4vw, 20px)', width: '100%', maxWidth: '100%' }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="animation-container" style={{ width: 'min(700px, 80vw, 80vh)', height: 'min(700px, 80vw, 80vh)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0', padding: '0', lineHeight: '0' }}>
            {loading ? (
              <div style={{ width: '48px', height: '48px', border: '3px solid #e5e7eb', borderTopColor: '#3a599c', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            ) : error || !animationData ? (
              <AlertCircle style={{ width: '120px', height: '120px', color: '#9ca3af' }} />
            ) : (
              <Lottie 
                animationData={animationData}
                loop={true}
                autoplay={true}
                style={{ width: '100%', height: '100%', margin: '0', padding: '0', display: 'block' }}
              />
            )}
          </div>
          
          <div className="button-container" style={{ position: 'absolute', bottom: 'clamp(12px, 2vw, 20px)', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'row', gap: 'clamp(12px, 2vw, 16px)', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
            <div className="tooltip-wrapper">
              <button
                onClick={() => router.push('/')}
                className="round-button primary-button"
                aria-label="Go to Homepage"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 'clamp(40px, 5vw, 56px)',
                  height: 'clamp(40px, 5vw, 56px)',
                  backgroundColor: '#844dfa',
                  color: 'white',
                  borderRadius: '50%',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                  transform: 'translateY(0)',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#370e8f';
                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(99, 102, 241, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#844dfa';
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.3)';
                }}
              >
                <Home className="round-button-icon" size={24} />
              </button>
              <span className="tooltip-text">Go to Homepage</span>
            </div>
            <div className="tooltip-wrapper">
              <button
                onClick={() => router.push('/contact-us')}
                className="round-button secondary-button"
                aria-label="Contact Us"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 'clamp(40px, 5vw, 56px)',
                  height: 'clamp(40px, 5vw, 56px)',
                  backgroundColor: 'white',
                  color: '#4b5563',
                  borderRadius: '50%',
                  border: '2px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  transform: 'translateY(0)',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                }}
              >
                <RiCustomerServiceFill className="round-button-icon" />
              </button>
              <span className="tooltip-text">Contact Us</span>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        :global(div[style*="lottie"]) {
          margin: 0 !important;
          padding: 0 !important;
        }
        .animation-container {
          min-width: 250px;
          min-height: 250px;
        }
        .tooltip-wrapper {
          position: relative;
          display: inline-block;
        }
        .tooltip-text {
          visibility: hidden;
          opacity: 0;
          background-color: #1f2937;
          color: white;
          text-align: center;
          border-radius: 6px;
          padding: 6px 12px;
          position: absolute;
          bottom: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          white-space: nowrap;
          font-size: 12px;
          font-weight: 500;
          z-index: 1000;
          transition: opacity 0.2s, visibility 0.2s;
          pointer-events: none;
        }
        .tooltip-text::after {
          content: "";
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-top-color: #1f2937;
        }
        .tooltip-wrapper:hover .tooltip-text {
          visibility: visible;
          opacity: 1;
        }
        @media (max-width: 768px) {
          .animation-container {
            width: min(500px, 80vw, 60vh) !important;
            height: min(500px, 80vw, 60vh) !important;
          }
          .button-container {
            bottom: clamp(8px, 1.5vw, 16px) !important;
            gap: clamp(10px, 1.5vw, 14px) !important;
          }
          .round-button {
            width: clamp(44px, 5.5vw, 52px) !important;
            height: clamp(44px, 5.5vw, 52px) !important;
          }
          .tooltip-text {
            font-size: 11px;
            padding: 5px 10px;
            bottom: calc(100% + 6px);
          }
        }
        @media (max-width: 480px) {
          .animation-container {
            width: min(350px, 85vw, 55vh) !important;
            height: min(350px, 85vw, 55vh) !important;
          }
          .button-container {
            bottom: clamp(6px, 1vw, 12px) !important;
            gap: clamp(8px, 1.2vw, 12px) !important;
          }
          .round-button {
            width: clamp(40px, 5vw, 48px) !important;
            height: clamp(40px, 5vw, 48px) !important;
          }
          .tooltip-text {
            font-size: 10px;
            padding: 4px 8px;
            bottom: calc(100% + 5px);
          }
        }
        @media (max-width: 320px) {
          .button-container {
            bottom: 8px !important;
            gap: 8px !important;
          }
          .round-button {
            width: 40px !important;
            height: 40px !important;
          }
        }
      `}</style>
      <style jsx global>{`
        svg {
          display: block;
          margin: 0;
          padding: 0;
        }
        .round-button-icon {
          width: clamp(18px, 2.2vw, 24px) !important;
          height: clamp(18px, 2.2vw, 24px) !important;
          flex-shrink: 0;
        }
        @media (max-width: 768px) {
          .round-button-icon {
            width: clamp(18px, 2.5vw, 22px) !important;
            height: clamp(18px, 2.5vw, 22px) !important;
          }
        }
        @media (max-width: 480px) {
          .round-button-icon {
            width: clamp(16px, 2.2vw, 20px) !important;
            height: clamp(16px, 2.2vw, 20px) !important;
          }
        }
        @media (max-width: 320px) {
          .round-button-icon {
            width: 18px !important;
            height: 18px !important;
          }
        }
      `}</style>
    </div>
  );
}

